// Install affordance for the mobile web app — gives the dashboard a permanent
// home-screen icon (a real standalone app) instead of a throwaway browser tab.
//
// Always offers an install path on phone web:
//   • Chrome fired beforeinstallprompt → one-tap "Install" button.
//   • iOS Safari → Share → Add to Home Screen hint.
//   • Otherwise (e.g. Android before the prompt is ready) → tell the user to
//     use the browser menu. Chrome's beforeinstallprompt does NOT fire on the
//     first visit, so without this fallback the banner would show nothing and
//     the user has no way to install.
//
// Hidden in Electron and once already installed. Dismiss persists in
// localStorage so it stays gone across sessions until the user clears data.
import { MoreVertical, Share, X } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@onestone/ui/button';
import { isElectron } from '@/lib/electron';
import { useInstallPrompt } from '@/lib/pwa';

const DISMISS_KEY = 'onestone-install-dismissed';

function dismissed(): boolean {
  try {
    return window.localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function InstallAppBanner() {
  const { canInstall, iosHint, standalone, promptInstall } = useInstallPrompt();
  const [hidden, setHidden] = useState<boolean>(dismissed);

  if (hidden || standalone || isElectron()) return null;

  const close = (): void => {
    setHidden(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* best-effort */
    }
  };

  return (
    <div className="flex items-center gap-3 border-b border-border bg-primary/5 px-3 py-2 text-xs">
      <img src="./logo-256.png" alt="" className="h-7 w-7 shrink-0 rounded-md" />
      {canInstall ? (
        <>
          <span className="min-w-0 flex-1 text-muted-foreground">Add OneStone to your home screen.</span>
          <Button size="sm" className="h-7 shrink-0 text-xs" onClick={() => void promptInstall()}>
            Install
          </Button>
        </>
      ) : iosHint ? (
        <span className="min-w-0 flex-1 text-muted-foreground">
          Install OneStone: tap <Share className="mx-0.5 inline h-3.5 w-3.5 align-text-bottom" aria-label="Share" />{' '}
          then <span className="font-medium text-foreground">Add to Home Screen</span>.
        </span>
      ) : (
        <span className="min-w-0 flex-1 text-muted-foreground">
          Install OneStone as an app: open the browser menu{' '}
          <MoreVertical className="mx-0.5 inline h-3.5 w-3.5 align-text-bottom" aria-label="menu" /> then tap{' '}
          <span className="font-medium text-foreground">Install app</span> (or{' '}
          <span className="font-medium text-foreground">Add to Home screen</span>).
        </span>
      )}
      <button
        type="button"
        onClick={close}
        aria-label="Dismiss"
        className="shrink-0 text-muted-foreground hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
